import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '../ui/input';
import {
  DEFAULT_TEXT_COLOR,
  hexToHsv,
  hexToRgb,
  hsvToHex,
  normalizeHexColor,
  parseRgbaString,
  rgbaString,
  rgbaToHex,
} from '../../utils/richEmailEditorUtils';

const PICKER_WIDTH = 248;
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const ColorPickerPopover = ({
  open,
  onClose,
  anchorRef,
  value = DEFAULT_TEXT_COLOR,
  onChange,
  mode = 'text',
}) => {
  const panelRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [hue, setHue] = useState(0);
  const [sat, setSat] = useState(0);
  const [val, setVal] = useState(0);
  const [alpha, setAlpha] = useState(1);

  const syncFromHex = useCallback((hex, nextAlpha = 1) => {
    const normalized = normalizeHexColor(hex) || DEFAULT_TEXT_COLOR;
    const hsv = hexToHsv(normalized);
    setHue(hsv.h);
    setSat(hsv.s);
    setVal(hsv.v);
    setAlpha(nextAlpha);
  }, []);

  useEffect(() => {
    if (!open) return;
    syncFromHex(value, 1);
  }, [open, value, syncFromHex]);

  const hex = useMemo(() => hsvToHex(hue, sat, val), [hue, sat, val]);
  const { r, g, b } = useMemo(() => hexToRgb(hex), [hex]);
  const rgbaLabel = useMemo(() => rgbaString(r, g, b, alpha), [r, g, b, alpha]);

  const emitColor = useCallback((nextHex, nextAlpha = alpha) => {
    const normalized = normalizeHexColor(nextHex);
    if (!normalized) return;
    const rgb = hexToRgb(normalized);
    onChange?.({
      hex: normalized,
      rgba: rgbaString(rgb.r, rgb.g, rgb.b, nextAlpha),
      alpha: nextAlpha,
    });
  }, [alpha, onChange]);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const left = Math.min(rect.left, window.innerWidth - PICKER_WIDTH - 12);
    const top = rect.bottom + 8;
    setPosition({ top, left: Math.max(12, left) });
  }, [anchorRef]);

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    const handleClickOutside = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose, anchorRef]);

  const handleSvChange = (nextSat, nextVal, emit = true) => {
    setSat(nextSat);
    setVal(nextVal);
    if (emit) emitColor(hsvToHex(hue, nextSat, nextVal));
  };

  const handleHueChange = (nextHue, emit = true) => {
    setHue(nextHue);
    if (emit) emitColor(hsvToHex(nextHue, sat, val));
  };

  const handleAlphaChange = (nextAlpha, emit = true) => {
    setAlpha(nextAlpha);
    if (emit) emitColor(hex, nextAlpha);
  };

  const bindDrag = (onMove) => (e) => {
    e.preventDefault();
    onMove(e);
    const move = (ev) => onMove(ev);
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  const handleHexInput = (next) => {
    const normalized = normalizeHexColor(next);
    if (!normalized) return;
    syncFromHex(normalized, alpha);
    emitColor(normalized, alpha);
  };

  const handleRgbaInput = (next) => {
    const parsed = parseRgbaString(next);
    const nextHex = rgbaToHex(parsed.r, parsed.g, parsed.b);
    if (!normalizeHexColor(nextHex)) return;
    syncFromHex(nextHex, parsed.a);
    emitColor(nextHex, parsed.a);
  };

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={panelRef}
      className="fixed z-[9999] rounded-lg border border-slate-200 bg-white p-3 shadow-xl"
      style={{ top: position.top, left: position.left, width: PICKER_WIDTH }}
      data-testid={`${mode}-color-picker`}
      onMouseDown={(e) => e.preventDefault()}
    >
      <div
        className="absolute -top-2 left-6 h-4 w-4 rotate-45 border-l border-t border-slate-200 bg-white"
        aria-hidden
      />
      <div className="flex gap-2">
        <div
          className="relative h-[168px] w-[168px] cursor-crosshair rounded-md border border-slate-200"
          style={{
            background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hue}, 100%, 50%))`,
          }}
          onMouseDown={bindDrag((ev) => {
            const rect = ev.currentTarget.getBoundingClientRect();
            const x = clamp((ev.clientX - rect.left) / rect.width, 0, 1);
            const y = clamp((ev.clientY - rect.top) / rect.height, 0, 1);
            handleSvChange(x * 100, (1 - y) * 100);
          })}
          data-testid="color-sv-square"
        >
          <div
            className="pointer-events-none absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-md ring-1 ring-slate-300"
            style={{
              left: `${sat}%`,
              top: `${100 - val}%`,
              backgroundColor: hex,
            }}
          />
        </div>
        <div className="flex flex-col gap-2">
          <div
            className="h-[80px] w-3 cursor-pointer rounded border border-slate-200"
            style={{ background: 'linear-gradient(to bottom, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)' }}
            onMouseDown={bindDrag((ev) => {
              const rect = ev.currentTarget.getBoundingClientRect();
              const y = clamp((ev.clientY - rect.top) / rect.height, 0, 1);
              handleHueChange(y * 360);
            })}
            data-testid="color-hue-slider"
          />
          <div
            className="relative h-[80px] w-3 cursor-pointer overflow-hidden rounded border border-slate-200"
            style={{
              backgroundImage:
                'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)',
              backgroundSize: '8px 8px',
              backgroundPosition: '0 0, 4px 4px',
            }}
            onMouseDown={bindDrag((ev) => {
              const rect = ev.currentTarget.getBoundingClientRect();
              const y = clamp((ev.clientY - rect.top) / rect.height, 0, 1);
              handleAlphaChange(1 - y);
            })}
            data-testid="color-alpha-slider"
          >
            <div
              className="absolute inset-0"
              style={{ background: `linear-gradient(to bottom, ${hex}, transparent)` }}
            />
          </div>
        </div>
      </div>

      <div
        className="mt-3 h-9 rounded-md border border-slate-200"
        style={{ backgroundColor: hex, opacity: alpha }}
        data-testid="color-preview-bar"
      />

      <div className="mt-3 space-y-2">
        <div className="flex overflow-hidden rounded-md border border-slate-200">
          <span className="flex w-12 shrink-0 items-center justify-center bg-slate-50 text-xs font-medium text-slate-500">
            HEX
          </span>
          <Input
            data-testid="color-hex-input"
            value={hex}
            onChange={(e) => handleHexInput(e.target.value)}
            className="h-9 rounded-none border-0 text-xs font-mono focus-visible:ring-0"
          />
        </div>
        <div className="flex overflow-hidden rounded-md border border-slate-200">
          <span className="flex w-12 shrink-0 items-center justify-center bg-slate-50 text-xs font-medium text-slate-500">
            RGBA
          </span>
          <Input
            data-testid="color-rgba-input"
            value={rgbaLabel}
            onChange={(e) => handleRgbaInput(e.target.value)}
            className="h-9 rounded-none border-0 text-xs font-mono focus-visible:ring-0"
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ColorPickerPopover;
